import "../css/FullEvent/FullEvent.css";
import "../css/GlobalStyles.css";
import {
  memo,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
  ReactNode,
} from "react";
import powered from "../assets/WhiteLabel/Powered.png";
import RCA from "../assets/WhiteLabel/RCA.png";
import { useDispatch, useSelector } from "react-redux";
import {
  LoginState,
  accountActions,
  getAccountState,
} from "../redux/slices/accountSlice";
import { useLocation, useNavigate } from "react-router-dom";
import { Colors } from "../utils/colors";
import { MixpanelContext } from "../context/AnalyticsService";
import { DarkTheme, LightTheme, useTheme } from "../hooks/useTheme";
import { ThemeType } from "@markit/common.types";
import MarkitHeaderLogo from "./MarkitHeaderLogo";
import { Icon } from "@iconify/react";
import {
  MARKIT_CONTACT_US,
  MARKIT_DOCS,
  MARKIT_IG,
} from "@markit/common.utils";
import ProfileHeaderDropdown from "./CreatorDashboard/CreatorProfile/ProfileHeaderDropdown";
import ProfilePic from "./ProfilePic";
import FatHeaderActionButton from "./Buttons/FatHeaderActionButton";
import { DropdownMenu, DropdownMenuItem } from "./DropdownMenu";
import { checkIsDesktop } from "../utils/screenResponsivenessUtils";
import { isDesktop } from "react-device-detect";
import MarkitLogoWhite from "../../src/assets/MarkitLogoWhite.png";
import MarkitLogoBlack from "../../src/assets/MarkitLogoBlack.png";
import {
  LandingPageNavigationId,
  LandingPageSourceLabels,
  NavigationId,
} from "../navigation/AppParamList";
import TextHoverButton from "./Buttons/TextHoverButton";
import MobileNavDrawer from "./Navigation/MobileNavDrawer";
import { LoginStage } from "../screens/Login/LoginWelcome";
import DropdownArrowIcon from "./DropdownsAndTabs/DropdownArrowIcon";

type TopHeaderProps = {
  showRCA?: boolean;
  eventTheme?: ThemeType;
  whiteLogo?: boolean;
  nonCreatorPage?: boolean; // ie. website pages, explore page, viewing own profile page
  preventLogoClick?: boolean;
  onMobileNavOpenChange?: (isOpen: boolean) => void;
};

const TopHeader = memo(function TopHeaderFn(props: TopHeaderProps) {
  const {
    showRCA,
    eventTheme,
    whiteLogo = false,
    nonCreatorPage = false,
    preventLogoClick = false,
    onMobileNavOpenChange,
  } = props;
  const mixpanel = useContext(MixpanelContext);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { account } = useSelector(getAccountState);
  const { isRCA, loggedIn, accountData } = account;

  const { theme } = useTheme(eventTheme);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [isPricingHovered, setIsPricingHovered] = useState(false);

  const styles = {
    buttonStyle: {
      padding: 10,
      height: 35,
      color: theme.PrimaryText.color,
      border: `0.5px solid ${theme.DividerColor.borderColor}`,
      backgroundColor: theme.NewPrimaryBG.backgroundColor,
      zIndex: 99,
    },
  };

  // Add useEffect to handle window resizing
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isDesktopSize = useMemo(
    () => checkIsDesktop(windowWidth),
    [windowWidth]
  );

  const onMarkitClick = useCallback(() => {
    if (mixpanel) {
      mixpanel.track("Webapp: Pressed Header Markit Button", {
        pathname: location.pathname,
      });
    }
    if (loggedIn === LoginState.LOGGED_IN && accountData.inCreatorMode) {
      navigate("/home");
    } else {
      navigate("/");
    }
  }, [
    accountData.inCreatorMode,
    location.pathname,
    loggedIn,
    mixpanel,
    navigate,
  ]);

  const signUpUser = useCallback(() => {
    if (mixpanel && nonCreatorPage) {
      mixpanel.track("Website: Pressed Sign Up", {
        source: LandingPageSourceLabels[location.pathname],
      });
    }
    dispatch(accountActions.setRedirectPath(location.pathname));
    navigate(NavigationId.WELCOME);
  }, [dispatch, location.pathname, mixpanel, nonCreatorPage, navigate]);

  const signInUser = useCallback(() => {
    if (mixpanel && nonCreatorPage) {
      mixpanel.track("Website: Pressed Sign In", {
        source: LandingPageSourceLabels[location.pathname],
      });
    }
    dispatch(accountActions.setRedirectPath(location.pathname));
    // Default to subscriber sign up flow if on a non creator page
    navigate(NavigationId.SIGN_IN + (nonCreatorPage ? "" : "?subscriber"), {
      state: {
        preLoginStage: nonCreatorPage ? undefined : LoginStage.PHONE,
      },
    });
  }, [dispatch, nonCreatorPage, location.pathname, mixpanel, navigate]);

  const exploreOnPress = useCallback(() => {
    if (mixpanel) {
      if (nonCreatorPage) {
        mixpanel.track("Website: Pressed Discover", {
          source: LandingPageSourceLabels[location.pathname],
        });
      } else {
        mixpanel.track("Webapp: Pressed Header Explore Button", {
          pathname: location.pathname,
        });
      }
    }
    navigate("/explore");
  }, [nonCreatorPage, location.pathname, mixpanel, navigate]);

  // TODO (jonathan): change back to a dropdown menu when we have more product pages
  // const productDropdownItems: DropdownMenuItem[] = useMemo(
  //   () => [
  //     {
  //       title: "Texting",
  //       key: "texting",
  //       onPress: () => navigate(LandingPageNavigationId.TEXTING),
  //     },
  //     {
  //       title: "Integrations",
  //       key: "integrations",
  //       onPress: () => window.open(MARKIT_AI + "integrations"),
  //     },
  //   ],
  //   [navigate]
  // );

  const getMarkitDocsMixpanel = useCallback(
    (page: string) => {
      if (mixpanel) {
        mixpanel.track("Website: Navigate To Markit Docs", {
          distinct_id: accountData.uid,
          source: LandingPageSourceLabels[location.pathname],
          docs_page: page,
        });
      }
    },
    [accountData.uid, location.pathname, mixpanel]
  );

  const dropdownSubtext = useCallback(
    (subtext: string) => (
      <span style={{ fontSize: 12, color: Colors.GRAY3 }}>{subtext}</span>
    ),
    []
  );

  const featuresDropdownItems: DropdownMenuItem[] = useMemo(
    () => [
      {
        title: "Texting",
        subtext: dropdownSubtext("Reach followers instantly"),
        key: "texting",
        onPress: () => {
          if (mixpanel) {
            mixpanel.track("Website: Navigate To Texting Product Page", {
              distinct_id: accountData.uid,
              source: LandingPageSourceLabels[location.pathname],
            });
          }
          navigate(LandingPageNavigationId.TEXTING);
        },
      },
      {
        title: "Monetization",
        subtext: dropdownSubtext("Make money texting"),
        key: "monetization",
        onPress: () => {
          navigate(LandingPageNavigationId.MONETIZATION);
        },
      },
    ],
    [dropdownSubtext, mixpanel, navigate, accountData.uid, location.pathname]
  );

  const resourcesDropdownItems: DropdownMenuItem[] = useMemo(
    () => [
      {
        title: "Documentation",
        subtext: dropdownSubtext("Visit our support center"),
        key: "documentation",
        onPress: () => {
          getMarkitDocsMixpanel("home");
          window.open(MARKIT_DOCS);
        },
      },
      {
        title: "Join our Discord",
        subtext: dropdownSubtext("Our creator community"),
        key: "join_discord",
        onPress: () => {
          getMarkitDocsMixpanel("discord");
          window.open(MARKIT_DOCS + "/joinourdiscord");
        },
      },
    ],
    [dropdownSubtext, getMarkitDocsMixpanel]
  );

  const aboutDropdownItems: DropdownMenuItem[] = useMemo(
    () => [
      {
        title: "Contact Support",
        subtext: dropdownSubtext("Get in touch with our team"),
        key: "contact_support",
        onPress: () => {
          getMarkitDocsMixpanel("contact support");
          window.open(MARKIT_CONTACT_US);
        },
      },
      {
        title: "Social Media",
        subtext: dropdownSubtext("Follow our journey on socials"),
        key: "social_media",
        onPress: () => {
          window.open(MARKIT_IG);
        },
      },
      {
        title: "Press and Media",
        subtext: dropdownSubtext("Markit in the news"),
        key: "press_and_media",
        onPress: () => navigate(LandingPageNavigationId.PRESS_MEDIA),
      },
    ],
    [dropdownSubtext, getMarkitDocsMixpanel, navigate]
  );

  const headerNavOnPress = useCallback(
    (item: string) => {
      if (item === "Pricing") {
        if (mixpanel) {
          mixpanel.track("Website: Navigate To Pricing Product Page", {
            distinct_id: accountData.uid,
            source: LandingPageSourceLabels[location.pathname],
          });
        }
        navigate(LandingPageNavigationId.PRICING);
      }
    },
    [accountData.uid, location.pathname, mixpanel, navigate]
  );

  const toggleMobileNav = useCallback(() => {
    const newState = !isMobileNavOpen;
    setIsMobileNavOpen(newState);
    onMobileNavOpenChange?.(newState);
  }, [isMobileNavOpen, onMobileNavOpenChange]);

  const handleDropdownToggle = useCallback(
    (dropdownName: string, isOpen: boolean) => {
      setOpenDropdown(isOpen ? dropdownName : null);
    },
    []
  );

  const renderHeaderButton = useCallback(
    (children: ReactNode) => (
      <div
        style={{ zIndex: 99 }}
        onMouseEnter={(e) =>
          theme === DarkTheme
            ? (e.currentTarget.style.filter = "brightness(0.7)")
            : null
        }
        onMouseLeave={(e) =>
          theme === DarkTheme
            ? (e.currentTarget.style.filter = "brightness(1)")
            : null
        }
      >
        {children}
      </div>
    ),
    [theme]
  );

  const renderSignInButton = useMemo(
    () =>
      renderHeaderButton(
        <FatHeaderActionButton
          title={<span style={{ fontSize: 12 }}>Sign In</span>}
          onPress={signInUser}
          containerStyles={{
            ...styles.buttonStyle,
            ...(isDesktopSize
              ? {}
              : {
                  border: "none",
                  color: theme.ButtonText.color,
                  backgroundColor: theme.ButtonBG.backgroundColor,
                }),
          }}
          hideOpacityChange={theme === DarkTheme}
        />
      ),
    [isDesktopSize, renderHeaderButton, signInUser, styles.buttonStyle, theme]
  );

  return (
    <div
      className={
        (isDesktop ? "FinalHeaderDesktop" : "FinalHeaderMobile") +
        " AlignedRowSpaced"
      }
      style={{
        ...(isRCA && showRCA
          ? {
              minHeight: isDesktop ? "100px" : "80px",
              marginRight: "20px",
            }
          : {}),
      }}
    >
      {nonCreatorPage && isDesktopSize ? (
        <div className="AlignedRow" style={{ gap: 20, zIndex: 99 }}>
          {["Features", "Pricing", "Resources", "About"].map((item) => (
            <DropdownMenu
              key={item}
              dropdownItems={
                item === "Features"
                  ? featuresDropdownItems
                  : item === "Resources"
                    ? resourcesDropdownItems
                    : item === "About"
                      ? aboutDropdownItems
                      : []
              }
              placement="bottomLeft"
              disabled={item === "Pricing"}
              dark={theme === DarkTheme}
              altWidth={180}
              onOpenChange={(isOpen) => handleDropdownToggle(item, isOpen)}
            >
              <div
                style={{
                  paddingInline: 8,
                  paddingBlock: 10,
                  borderRadius: 8,
                  backgroundColor:
                    openDropdown === item ||
                    (item === "Pricing" && isPricingHovered)
                      ? theme.NewPrimaryBG.backgroundColor
                      : Colors.TRANSPARENT,
                  cursor: "pointer",
                }}
                onMouseEnter={() =>
                  item === "Pricing" ? setIsPricingHovered(true) : null
                }
                onMouseLeave={() =>
                  item === "Pricing" ? setIsPricingHovered(false) : null
                }
              >
                <TextHoverButton
                  text={item}
                  onPress={() => headerNavOnPress(item)}
                  textStyles={{
                    fontSize: 14,
                    fontWeight: 500,
                    ...theme.PrimaryText,
                  }}
                  icon={
                    item !== "Pricing" ? (
                      <DropdownArrowIcon
                        isOpen={openDropdown === item}
                        height={14}
                        color={theme.PrimaryText.color}
                        style={{ marginBottom: -2 }}
                      />
                    ) : undefined
                  }
                  iconPosition="right"
                />
              </div>
            </DropdownMenu>
          ))}
        </div>
      ) : null}
      <div
        style={
          nonCreatorPage && isDesktopSize
            ? {
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
              }
            : {}
        }
      >
        {isRCA && showRCA ? (
          <div className="AlignedRow">
            <div
              style={{
                height: isDesktop ? "90px" : "60px",
              }}
            >
              <img
                src={RCA}
                className="referralProgramCircle"
                alt="ReferralProgramImage"
              />
            </div>
            <div
              onClick={onMarkitClick}
              style={{ height: isDesktop ? "40px" : "30px" }}
            >
              <img
                src={powered}
                className="referralProgramCircle"
                alt="ReferralProgramImage"
              />
            </div>
          </div>
        ) : (
          <MarkitHeaderLogo
            theme={theme}
            alternateLogo={
              theme === LightTheme ? MarkitLogoBlack : MarkitLogoWhite
            }
            alternateSize={isDesktopSize ? 24 : 20}
            disabled={preventLogoClick}
          />
        )}
      </div>
      <div className="AlignedRow" style={{ gap: 10 }}>
        {renderHeaderButton(
          <FatHeaderActionButton
            title={<span style={{ fontSize: 12 }}>Explore</span>}
            leftIcon
            icon={
              <Icon
                icon={"ion:compass"}
                height={13}
                style={{ color: theme.PrimaryText.color }}
              />
            }
            onPress={exploreOnPress}
            containerStyles={styles.buttonStyle}
            hideOpacityChange={theme === DarkTheme}
          />
        )}
        {loggedIn === LoginState.LOGGED_IN ? (
          <ProfileHeaderDropdown
            placement="bottom"
            eventTheme={eventTheme}
            nonCreatorPage={nonCreatorPage}
          >
            <ProfilePic
              user={accountData}
              forcePointer
              size={35}
              blueGradientBackground
            />
          </ProfileHeaderDropdown>
        ) : nonCreatorPage ? (
          <>
            {isDesktopSize ? renderSignInButton : null}
            {renderHeaderButton(
              <FatHeaderActionButton
                title={<span style={{ fontSize: 12 }}>Get Started</span>}
                onPress={signUpUser}
                containerStyles={{
                  ...styles.buttonStyle,
                  border: "none",
                  color: theme.ButtonText.color,
                  backgroundColor: theme.ButtonBG.backgroundColor,
                }}
                hideOpacityChange={theme === DarkTheme}
              />
            )}
          </>
        ) : (
          renderSignInButton
        )}
        {nonCreatorPage && !isDesktopSize ? (
          <div onClick={toggleMobileNav} style={{ cursor: "pointer" }}>
            <Icon
              icon={isMobileNavOpen ? "mdi:close" : "mdi:menu"}
              height={24}
              style={{
                color: whiteLogo ? Colors.WHITE : theme.PrimaryText.color,
              }}
            />
          </div>
        ) : null}
      </div>
      {!isDesktopSize ? (
        <MobileNavDrawer
          isOpen={isMobileNavOpen}
          onClose={() => toggleMobileNav()}
          featuresDropdownItems={featuresDropdownItems}
          resourcesDropdownItems={resourcesDropdownItems}
          aboutDropdownItems={aboutDropdownItems}
          headerNavOnPress={headerNavOnPress}
          exploreOnPress={exploreOnPress}
          signUpUser={signUpUser}
        />
      ) : null}
    </div>
  );
});

export default TopHeader;
